import {
    TargetableObjectNode,
    constrainTransformTargetableNode,
    TransformMode,
    transformValue,
    isTemplateNode,
    ObjectNode,
    TransformMatrix,
    isNodeWithMatrixArrayLockedTransformParameter,
    isTransformLike,
    transformLike,
} from "@cm/template-nodes"
import {Component, computed, output, ChangeDetectionStrategy} from "@angular/core"
import {narrowInstance} from "@cm/utils"
import {Matrix4} from "@cm/math"
import {BaseInspectorComponent} from "@template-editor/components/inspectors/base-inspector/base-inspector.component"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {ValueSlotComponent} from "@template-editor/components/value-slot/value-slot.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-object-inspector",
    templateUrl: "./object-inspector.component.html",
    styleUrl: "./object-inspector.component.scss",
    imports: [InspectorSectionComponent, ValueSlotComponent],
})
export class ObjectInspectorComponent extends BaseInspectorComponent<ObjectNode> {
    readonly $lockedTransform = computed(() => {
        return this.$parameters().lockedTransform
    })

    readonly $targetableNode = computed(() => narrowInstance(this.$node(), TargetableObjectNode))

    readonly $isTargetMode = computed(() => this.sceneManagerService.getPartForSelectedNode(this.$node()) === "target")

    readonly $targetableNodeTransform = computed(() => {
        const targetableNode = this.$targetableNode()
        if (!targetableNode) return undefined
        const lockedTransform = this.$lockedTransform()

        if (this.$isTargetMode() && !(lockedTransform instanceof TransformMatrix)) return Matrix4.translation(...targetableNode.parameters.target).toArray()
        else return lockedTransform
    })

    onTargetableNodeLockedTransformChanged(value: unknown) {
        if (isTransformLike(value)) {
            if (isTemplateNode(value)) {
                const node = this.$node()
                this.sceneManagerService.modifyTemplateGraph(() => {
                    node.updateParameters({
                        lockedTransform: value,
                    })
                })
                return
            }

            const parsedValue = transformValue.safeParse(value)
            if (!parsedValue.success) return

            const transformMode = this.sceneManagerService.$transformMode()
            this.sceneManagerService.modifyTemplateGraph(() => {
                const targetableNode = this.$targetableNode()
                if (!targetableNode) return
                const transform = new Matrix4(parsedValue.data)

                if (isNodeWithMatrixArrayLockedTransformParameter(targetableNode)) {
                    const {transform: constrainedTransform, target: constrainedTarget} = constrainTransformTargetableNode(
                        this.sceneManagerService,
                        transform,
                        {templateNode: targetableNode, part: this.$isTargetMode() ? "target" : "root"},
                        transformMode,
                    )
                    if (constrainedTransform) targetableNode.updateParameters({lockedTransform: constrainedTransform.toArray()})
                    if (constrainedTarget) targetableNode.updateParameters({target: constrainedTarget.toArray()})
                } else {
                    targetableNode.updateParameters({lockedTransform: transform.toArray()})
                }
            })
        }
    }

    readonly transformModeChanged = output<TransformMode>()

    protected readonly transformLike = transformLike
}
